import React, { useState } from 'react';
import { 
  Button, 
  ButtonGroup, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText
} from '@mui/material';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';

const WorkflowActions = ({ order, onStatusChange, onCreateProof, onCreateQuote, onClose, renderAsMenuItems = false }) => {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const handleOpen = (actionType) => {
    // Skip the dialog — execute the status change immediately
    handleSubmit(actionType);
  };

  const handleClose = () => {
    setOpen(false);
    setNotes('');
  };

  const handleSubmit = async (actionType) => {
    const resolvedAction = actionType || action;
    const orderType = order.orderType?.toLowerCase() || '';

    // For Quote action on stationery orders: skip the status change and go straight to
    // quote creation. Status is set to 'Quote Active' by the server when the quote is
    // actually generated, so cancelling out of the form leaves the order untouched.
    if (resolvedAction === 'quote' && orderType === 'stationery' && onCreateQuote) {
      onCreateQuote(order.id);
      return;
    }

    // For Proof action: skip the status change and go straight to proof creation.
    // Status is set to 'Proof Active' by the server when the proof is actually generated,
    // so cancelling out of the form leaves the order untouched.
    if (resolvedAction === 'proof' && onCreateProof) {
      onCreateProof(order.id);
      return;
    }

    setLoading(true);
    try {
      // Format the status consistently - capitalize first letter of each word
      const formattedStatus = resolvedAction
        .split(/[_\s]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Import CSRF protection
      const { fetchCSRFToken } = await import('../../utils/csrfProtection');
      const csrfToken = await fetchCSRFToken();
      
      // Determine the correct endpoint based on order type
      let endpoint = `${config.apiUrl}/api/ad-orders/${order.id}/status`; // default to ad orders
      
      if (orderType === 'stationery') {
        endpoint = `${config.apiUrl}/api/stationery-orders/${order.id}/status`;
      } else if (orderType === 'school') {
        endpoint = `${config.apiUrl}/api/school-orders/${order.id}/status`;
      }
      
      await axios.post(endpoint, {
        status: formattedStatus,
        notes: ''
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        }
      });
      
      // If successful, call the onStatusChange callback
      if (onStatusChange) {
        onStatusChange(formattedStatus, '');
      }
      
      handleClose();
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if we should use the sales workflow or the production workflow
  // based on the current status of the order
  const shouldUseSalesWorkflow = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const salesStatuses = ['prospect', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
    
    // If the current status is in the sales workflow, use it
    if (salesStatuses.includes(currentStatus)) {
      return true;
    }
    
    // For production workflow statuses, use production workflow
    const productionStatuses = ['new', 'waiting for artwork', 'in design', 'proofing', 'proof pending', 'proof_pending', 'approved', 'uploaded', 'invoiced', 'cancelled'];
    if (productionStatuses.includes(currentStatus)) {
      return false;
    }
    
    // Default to production workflow for unknown statuses (most orders are production workflow)
    return false;
  };

  // Define available actions based on current status
  const getAvailableActions = () => {
    // Use the appropriate workflow based on the current status
    if (shouldUseSalesWorkflow()) {
      return getSalesWorkflowActions();
    } else {
      return getProductionWorkflowActions();
    }
  };
  
  // Sales workflow actions (prospect, qualified, proposal, etc.)
  const getSalesWorkflowActions = () => {
    const currentStatus = order.status?.toLowerCase() || 'prospect';
    
    switch (currentStatus) {
      case 'prospect':
      case 'new':
        return [
          { label: 'Qualified', value: 'qualified', color: 'warning' },
          { label: 'Proposal', value: 'proposal', color: 'info' },
          { label: 'Negotiation', value: 'negotiation', color: 'primary' },
          { label: 'Won', value: 'closed_won', color: 'success' },
          { label: 'Lost', value: 'closed_lost', color: 'error' }
        ];
      case 'qualified':
        return [
          { label: 'Proposal', value: 'proposal', color: 'info' },
          { label: 'Negotiation', value: 'negotiation', color: 'primary' },
          { label: 'Won', value: 'closed_won', color: 'success' },
          { label: 'Lost', value: 'closed_lost', color: 'error' }
        ];
      case 'proposal':
        return [
          { label: 'Negotiation', value: 'negotiation', color: 'primary' },
          { label: 'Won', value: 'closed_won', color: 'success' },
          { label: 'Lost', value: 'closed_lost', color: 'error' }
        ];
      case 'negotiation':
        return [
          { label: 'Won', value: 'closed_won', color: 'success' },
          { label: 'Lost', value: 'closed_lost', color: 'error' }
        ];
      case 'closed_won':
        return [
          { label: 'Production', value: 'new', color: 'primary' },
          { label: 'Lost', value: 'closed_lost', color: 'error' }
        ];
      case 'closed_lost':
        return [
          { label: 'New', value: 'prospect', color: 'primary' }
        ];
      default:
        return [
          { label: 'New', value: 'prospect', color: 'primary' }
        ];
    }
  };
  
  // Production workflow actions (new, waiting for artwork, in design, etc.)
  const getProductionWorkflowActions = () => {
    const currentStatus = order.status?.toLowerCase() || 'new';
    const orderType = order.orderType?.toLowerCase() || '';
    
    // AD ORDERS WORKFLOW
    if (orderType === 'ad') {
      switch (currentStatus) {
        case 'new':
          return [
            { label: 'Design', value: 'in design', color: 'info', customColor: '#FDD835' },        // Yellow
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'in design':
          return [
            { label: 'Proof', value: 'proof', color: 'secondary', customColor: '#CE93D8' },         // Light Purple
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'proof':
          // Generate Proof button shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'proof active':
          // View Proof and Cancel Proof buttons shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'proof needs changes':
          // View Changes and Generate Proof buttons shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'invoiced':
          return [
            { label: 'Upload', value: 'uploaded', color: 'success', customColor: '#66BB6A' },       // Green
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
        case 'uploaded':
        case 'paid':
        case 'cancelled':
          return [];
        default:
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }         // Red
          ];
      }
    }
    
    // STATIONERY ORDERS WORKFLOW
    if (orderType === 'stationery') {
      switch (currentStatus) {
        case 'new':
          return [
            { label: 'Quote', value: 'quote', color: 'warning', customColor: '#FFB74D' }, // Light Orange
            { label: 'Design', value: 'in design', color: 'warning', customColor: '#FDD835' }, // Yellow
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'quote':
        case 'quoting':
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'quote active':
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'quote needs changes':
        case 'changes needed':
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'quote declined':
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'quote accepted':
        case 'quote approved':
          return [
            { label: 'Design', value: 'in design', color: 'warning', customColor: '#FDD835' }, // Yellow
            { label: 'Proof', value: 'proof', color: 'secondary', customColor: '#CE93D8' }, // Light Purple
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'in design':
          return [
            { label: 'Proof', value: 'proof', color: 'secondary', customColor: '#CE93D8' }, // Light Purple
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'proof':
        case 'proofing':
          // Generate Proof button will be shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'proof active':
          // View Proof and Cancel Proof buttons will be shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'proof needs changes':
          // Generate Proof button and View Changes will be shown separately
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'proof accepted':
        case 'proof approved':
          return [
            { label: 'Production', value: 'in production', color: 'warning', customColor: '#FDD835' }, // Yellow
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'in production':
          // Invoice button will be shown separately (opens invoice generation page)
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'invoiced':
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' } // Red
          ];
        case 'cancelled':
          // Cancelled status has no further actions
          return [];
        default:
          return [
            { label: 'New', value: 'new', color: 'primary' }
          ];
      }
    }
    
    // UPDATE ORDERS WORKFLOW — mirrors Ad Orders
    if (orderType === 'update') {
      // Determine if this update type requires proofing (Artwork only)
      // Dates and Link updates go straight to Upload without a proof step
      const orderUpdateType = (order.orderUpdateType || '').toLowerCase();
      const requiresProof = orderUpdateType.includes('artwork') && 
                            !orderUpdateType.includes('date') && 
                            !orderUpdateType.includes('link');

      switch (currentStatus) {
        case 'new':
          return [
            { label: 'Design', value: 'in design', color: 'info', customColor: '#FDD835' },
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'in design':
          if (requiresProof) {
            return [
              { label: 'Proof', value: 'proof', color: 'secondary', customColor: '#CE93D8' },
              { label: 'Upload', value: 'uploaded', color: 'success', customColor: '#66BB6A' },
              { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
            ];
          }
          // Dates / Link updates — skip proof, go straight to Upload
          return [
            { label: 'Upload', value: 'uploaded', color: 'success', customColor: '#66BB6A' },
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'proof':
          // Generate Proof button shown separately (Artwork updates only)
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'proof active':
          // View Proof and Cancel Proof buttons shown separately (Artwork updates only)
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'proof needs changes':
          // View Changes and Generate Proof buttons shown separately (Artwork updates only)
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'invoiced':
          return [
            { label: 'Upload', value: 'uploaded', color: 'success', customColor: '#66BB6A' },
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
        case 'uploaded':
        case 'paid':
        case 'cancelled':
          return [];
        default:
          return [
            { label: 'Cancel', value: 'cancelled', color: 'error', customColor: '#f44336' }
          ];
      }
    }
    
    // Default/fallback workflow for other order types
    switch (currentStatus) {
      case 'new':
        return [
          { label: 'Waiting for Artwork', value: 'waiting for artwork', color: 'warning' },
          { label: 'In Design', value: 'in design', color: 'info' },
          { label: 'Proofing', value: 'proofing', color: 'primary' },
          { label: 'Approved', value: 'approved', color: 'success' },
          { label: 'Cancel', value: 'cancelled', color: 'error' }
        ];
      case 'cancelled':
        return [
          { label: 'New', value: 'new', color: 'primary' }
        ];
      default:
        return [
          { label: 'New', value: 'new', color: 'primary' }
        ];
    }
  };

  const availableActions = getAvailableActions();

  // If no actions are available, don't render anything
  if (availableActions.length === 0) {
    return null;
  }

  // Get the dialog title and message based on the action
  const getDialogTitle = () => {
    // Sales workflow actions
    if (action === 'prospect') return 'Set as New';
    if (action === 'qualified') return 'Set to Qualified';
    if (action === 'proposal') return 'Move to Proposal';
    if (action === 'negotiation') return 'Move to Negotiation';
    if (action === 'closed_won') return 'Mark as Won';
    if (action === 'closed_lost') return 'Mark as Lost';
    
    // Production workflow actions
    if (action === 'new') return 'Set as New';
    if (action === 'waiting for artwork') return 'Set to Waiting for Artwork';
    if (action === 'in design') return 'Move to In Design';
    if (action === 'proofing') return 'Move to Proofing';
    if (action === 'proof') return 'Move to Proof';
    if (action === 'proof active') return 'Set Proof Active';
    if (action === 'proof approved') return 'Approve Proof';
    if (action === 'approved') return 'Approve Order';
    if (action === 'quoting') return 'Move to Quoting';
    if (action === 'quote active') return 'Set Quote Active';
    if (action === 'quote approved') return 'Approve Quote';
    if (action === 'in production') return 'Move to Production';
    if (action === 'uploaded') return 'Mark as Uploaded';
    if (action === 'invoiced') return 'Mark as Invoiced';
    if (action === 'paid') return 'Mark as Paid';
    if (action === 'shipped') return 'Mark as Shipped';
    if (action === 'cancelled') return 'Cancel Order';
    
    return 'Update Order Status';
  };
  
  const getDialogMessage = () => {
    // Sales workflow actions
    if (action === 'prospect') return 'Are you sure you want to set this order as New?';
    if (action === 'qualified') return 'Are you sure you want to mark this order as Qualified?';
    if (action === 'proposal') return 'Are you sure you want to move this order to Proposal?';
    if (action === 'negotiation') return 'Are you sure you want to move this order to Negotiation?';
    if (action === 'closed_won') return 'Are you sure you want to mark this order as Won?';
    if (action === 'closed_lost') return 'Are you sure you want to mark this order as Lost?';
    
    // Production workflow actions
    if (action === 'new') return 'Are you sure you want to set this order as New?';
    if (action === 'waiting for artwork') return 'Are you sure you want to mark this order as Waiting for Artwork?';
    if (action === 'in design') return 'Are you sure you want to move this order to In Design?';
    if (action === 'proofing') return 'Are you sure you want to move this order to Proofing?';
    if (action === 'proof') return 'Are you sure you want to move this order to Proof?';
    if (action === 'proof active') return 'Are you sure you want to set this order as Proof Active?';
    if (action === 'proof approved') return 'Are you sure you want to approve this proof?';
    if (action === 'approved') return 'Are you sure you want to approve this order?';
    if (action === 'quoting') return 'Are you sure you want to move this order to Quoting?';
    if (action === 'quote active') return 'Are you sure you want to set this order as Quote Active?';
    if (action === 'quote approved') return 'Are you sure you want to approve this quote?';
    if (action === 'in production') return 'Are you sure you want to move this order to Production?';
    if (action === 'uploaded') return 'Are you sure you want to mark this order as Uploaded? This will archive the order.';
    if (action === 'invoiced') return 'Are you sure you want to mark this order as Invoiced?';
    if (action === 'paid') return 'Are you sure you want to mark this order as Paid?';
    if (action === 'cancelled') return 'Are you sure you want to cancel this order? This will archive the order.';
    
    return 'Are you sure you want to update this order\'s status?';
  };

  // Check if we should show Generate Proof button
  const shouldShowGenerateProof = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const orderType = order.orderType?.toLowerCase() || '';
    
    // Show for ad orders in 'proof' or 'proof needs changes' status
    // Note: clicking "Proof" from 'in design' now skips directly to proof creation,
    // but orders may still land in 'proof' status via other paths (e.g. legacy data)
    if (orderType === 'ad' && (currentStatus === 'proof' || currentStatus === 'proof needs changes') && onCreateProof) {
      return true;
    }
    
    // Show for stationery orders in 'proof', 'proofing', or 'proof needs changes' status
    if (orderType === 'stationery' && 
        (currentStatus === 'proof' || currentStatus === 'proofing' || currentStatus === 'proof needs changes') && 
        onCreateProof) {
      return true;
    }
    
    // Show for update orders in proof status that need proofing (Artwork only, not Dates/Link)
    if (orderType === 'update' && (currentStatus === 'proof' || currentStatus === 'proof pending' || currentStatus === 'proof needs changes') && onCreateProof) {
      const orderUpdateType = (order.orderUpdateType || '').toLowerCase();
      const requiresProof = orderUpdateType.includes('artwork') &&
                            !orderUpdateType.includes('date') &&
                            !orderUpdateType.includes('link');
      if (requiresProof) {
        return true;
      }
    }
    
    return false;
  };

  // Check if we should show Generate Invoice button
  const shouldShowGenerateInvoice = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const orderType = order.orderType?.toLowerCase() || '';
    // Stationery orders in 'in production'
    if (orderType === 'stationery' && currentStatus === 'in production') return true;
    // Artwork-supplied update orders that have been set to Uploaded — ready to invoice directly
    if (orderType === 'update' && currentStatus === 'uploaded' &&
        order.artwork_cost_type === 'artwork_supplied') return true;
    // Ad orders deferred to invoice later, or where the automatic Xero invoice
    // failed to generate and the order was reverted to "Approved" — no invoice
    // exists yet either way, so let an admin trigger it manually.
    if (orderType === 'ad' && !order.xero_invoice_url &&
        (order.invoiceLater || currentStatus === 'approved')) return true;
    return false;
  };
  
  // Handle Generate Invoice action for stationery/ad orders
  const handleGenerateInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const orderType = order.orderType?.toLowerCase() || '';
      
      if (orderType === 'stationery') {
        if (!window.confirm('Generate invoice for this stationery order? The order status will be updated to Invoiced.')) {
          return;
        }
        
        // Generate invoice for stationery order
        const response = await axios.post(
          `${config.apiUrl}/api/stationery-orders/invoice/generate/${order.id}`,
          {},
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data && response.data.success) {
          alert('Invoice generated successfully! Order status updated to Invoiced.');
          if (onStatusChange) {
            onStatusChange('invoiced');
          }
          window.location.reload();
        } else {
          throw new Error(response.data?.message || 'Failed to generate invoice');
        }
      } else if (orderType === 'ad') {
        if (!window.confirm('Generate invoice for this order now? This will attempt to push it to Xero.')) {
          return;
        }

        const response = await axios.post(
          `${config.apiUrl}/api/ad-orders/${order.id}/generate-invoice`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data && response.data.success) {
          if (response.data.invoiceUrl) {
            window.open(response.data.invoiceUrl, '_blank');
          } else if (response.data.xeroError) {
            alert(`Order approved, but the Xero invoice was not created:\n\n${response.data.xeroError}`);
          }
          if (onStatusChange) {
            onStatusChange(response.data.invoiceUrl ? 'invoiced' : 'approved');
          }
          window.location.reload();
        } else {
          throw new Error(response.data?.message || 'Failed to generate invoice');
        }
      } else {
        // For update orders, navigate to the invoice creation page
        window.location.href = `/app/invoice/create/${order.id}`;
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice: ' + (error.response?.data?.message || error.message));
    }
  };

  // Check if we should show View Changes button for quote or proof changes
  const shouldShowViewChanges = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const orderType = order.orderType?.toLowerCase() || '';
    // Show for ad orders in 'proof needs changes'
    if (orderType === 'ad' && currentStatus === 'proof needs changes') return true;
    // Show for stationery orders in 'quote needs changes' or 'proof needs changes' status
    return orderType === 'stationery' && 
           (currentStatus === 'quote needs changes' || 
            currentStatus === 'changes needed' || 
            currentStatus === 'proof needs changes');
  };

  
  // Check if we should show View Proof button
  const shouldShowViewProof = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const orderType = order.orderType?.toLowerCase() || '';
    // Show for ad orders and update orders in 'proof active' status
    return (orderType === 'ad' || orderType === 'update') && currentStatus === 'proof active' && order.proofLink;
  };
  
  // Check if we should show Cancel Proof button
  const shouldShowCancelProof = () => {
    const currentStatus = order.status?.toLowerCase() || '';
    const orderType = order.orderType?.toLowerCase() || '';
    // Show for ad orders and update orders in 'proof active' status
    return (orderType === 'ad' || orderType === 'update') && currentStatus === 'proof active';
  };
  
  // Check if we should show View Quote button
  const shouldShowViewQuote = () => {
    // Quote actions are handled as menu items in StationeryOrders page
    return false;
  };
  
  // Check if we should show Cancel Quote button
  const shouldShowCancelQuote = () => {
    // Quote actions are handled as menu items in StationeryOrders page
    return false;
  };

  // Handle View Changes action (shows customer change requests from proof)
  const handleViewChanges = async () => {
    if (onClose) onClose();
    try {
      const orderResponse = await axios.get(
        `${config.apiUrl}/api/ad-orders/${order.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (orderResponse.data && (orderResponse.data.success || orderResponse.data.data)) {
        const orderData = orderResponse.data.data || orderResponse.data;
        if (orderData.notes) {
          const notesLines = orderData.notes.split('\n');
          const changeRequests = [];
          let currentRequest = null;
          notesLines.forEach(line => {
            const timestampMatch = line.match(/^\[(.*?)\]\s*(?:Customer Change Request|CLIENT CHANGE REQUEST):/i);
            if (timestampMatch) {
              if (currentRequest) changeRequests.push(currentRequest);
              currentRequest = { timestamp: timestampMatch[1], text: '' };
            } else if (currentRequest && line.trim()) {
              currentRequest.text += (currentRequest.text ? '\n' : '') + line;
            }
          });
          if (currentRequest) changeRequests.push(currentRequest);
          if (changeRequests.length > 0) {
            const changesList = changeRequests.map((r, i) => `${i + 1}. ${r.text}\n   Date: ${r.timestamp}`).join('\n\n');
            alert(`Client Change Requests:\n\n${changesList}`);
            return;
          }
        }
      }
      alert('No change requests found for this order.');
    } catch (error) {
      console.error('Error fetching change requests:', error);
      alert('Failed to fetch change requests: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle View Proof action
  const handleViewProof = () => {
    if (order.proofLink) {
      window.open(order.proofLink, '_blank');
    }
    if (onClose) onClose();
  };
  
  // Handle Cancel Proof action
  const handleCancelProof = async () => {
    if (!window.confirm('Are you sure you want to cancel this proof? This will deactivate the proof link and change the status back to "Proof".')) {
      return;
    }
    
    if (onClose) onClose();
    
    try {
      // Call backend API to cancel the proof
      const response = await axios.post(
        `${config.apiUrl}/api/proof/cancel/${order.id}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        // Tell the parent to update this order's status to 'Proof' in its state.
        // Pass _skipProofCreation so handleStatusChange doesn't re-enter the proof
        // creation flow (which it would for ad/update orders with status 'proof').
        if (onStatusChange) {
          onStatusChange('Proof', { _skipProofCreation: true });
        }
        alert('Proof cancelled successfully');
      }
    } catch (error) {
      console.error('Error cancelling proof:', error);
      alert('Failed to cancel proof: ' + (error.response?.data?.message || error.message));
    }
  };
  
  // Handle View Quote action
  const handleViewQuote = () => {
    if (order.quoteLink) {
      window.open(order.quoteLink, '_blank');
    }
    if (onClose) onClose();
  };
  
  // Handle Cancel Quote action
  const handleCancelQuote = async () => {
    if (!window.confirm('Are you sure you want to cancel this quote? This will deactivate the quote link and set the status back to Quoting.')) {
      return;
    }

    if (onClose) onClose();
    
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/quote/cancel-admin/${order.id}`,
        { reason: 'Quote cancelled by admin' },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        // Update local state by calling onStatusChange
        if (onStatusChange) {
          onStatusChange(order.id, 'Quoting');
        }
        alert('Quote cancelled successfully. Status has been reverted to "Quoting".');
        
        // Reload the page to refresh the data
        window.location.reload();
      } else {
        throw new Error(response.data?.message || 'Failed to cancel quote');
      }
    } catch (error) {
      console.error('Error cancelling quote:', error);
      alert('Failed to cancel quote: ' + (error.response?.data?.message || error.message));
    }
  };

  // Render as menu items if requested
  if (renderAsMenuItems) {
    return (
      <>
        {/* Show View Proof button */}
        {shouldShowViewProof() && (
          <MenuItem onClick={handleViewProof}>
            <ListItemText>View Proof</ListItemText>
          </MenuItem>
        )}
        
        {/* Show Cancel Proof button */}
        {shouldShowCancelProof() && (
          <MenuItem onClick={handleCancelProof}>
            <ListItemText>Cancel Proof</ListItemText>
          </MenuItem>
        )}
        
        {/* Show View Quote button */}
        {shouldShowViewQuote() && (
          <MenuItem onClick={handleViewQuote}>
            <ListItemText>View Quote</ListItemText>
          </MenuItem>
        )}
        
        {/* Show Cancel Quote button */}
        {shouldShowCancelQuote() && (
          <MenuItem onClick={handleCancelQuote}>
            <ListItemText>Cancel Quote</ListItemText>
          </MenuItem>
        )}
        
        {/* Show View Changes menu item */}
        {shouldShowViewChanges() && (
          <MenuItem onClick={handleViewChanges}>
            <ListItemText>View Changes</ListItemText>
          </MenuItem>
        )}

        {/* Show Generate Proof menu item */}
        {shouldShowGenerateProof() && (
          <MenuItem onClick={() => { onCreateProof(order.id); onClose && onClose(); }}>
            <ListItemText>Generate Proof</ListItemText>
          </MenuItem>
        )}
        
        {/* Show Generate Invoice menu item */}
        {shouldShowGenerateInvoice() && (
          <MenuItem onClick={() => { 
            if (onClose) onClose();
            handleGenerateInvoice();
          }}>
            <ListItemText>Generate Invoice</ListItemText>
          </MenuItem>
        )}
        
        {availableActions.map((actionItem) => (
          <MenuItem 
            key={actionItem.value}
            onClick={() => handleOpen(actionItem.value)}
          >
            <ListItemText>{actionItem.label}</ListItemText>
          </MenuItem>
        ))}

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>
            {getDialogTitle()}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {getDialogMessage()}
            </DialogContentText>
            <Box sx={{ mt: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                id="notes"
                label="Notes"
                type="text"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              color="primary" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Default render as button group
  return (
    <>
      <ButtonGroup size="small" variant="text">
        {/* Show View Proof button */}
        {shouldShowViewProof() && (
          <Button
            onClick={handleViewProof}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: '#9C27B0',
              color: '#fff',
              '&:hover': {
                bgcolor: '#9C27B0',
                filter: 'brightness(0.9)'
              }
            }}
          >
            View Proof
          </Button>
        )}
        
        {/* Show Cancel Proof button */}
        {shouldShowCancelProof() && (
          <Button
            onClick={handleCancelProof}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: '#CE93D8',
              color: '#000',
              '&:hover': {
                bgcolor: '#CE93D8',
                filter: 'brightness(0.9)'
              }
            }}
          >
            Cancel Proof
          </Button>
        )}
        
        {/* Show Gen Quote button for stationery orders with quote changes */}
        {order.orderType?.toLowerCase() === 'stationery' &&
          ['quote needs changes', 'changes needed'].includes(order.status?.toLowerCase().trim()) &&
          onCreateQuote && (
          <Button
            onClick={() => onCreateQuote(order.id)}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: '#FFB74D',
              color: '#000',
              '&:hover': {
                bgcolor: '#FFB74D',
                filter: 'brightness(0.9)'
              }
            }}
          >
            Gen Quote
          </Button>
        )}

        {/* Show Generate Proof button for advertising orders in proofing status */}
        {shouldShowGenerateProof() && (
          <Button
            color="secondary"
            onClick={() => onCreateProof(order.id)}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: '#CE93D8',
              color: '#000',
              '&:hover': {
                bgcolor: '#CE93D8',
                filter: 'brightness(0.9)'
              }
            }}
          >
            Gen Proof
          </Button>
        )}
        
        {/* Show Generate Invoice button for in production orders */}
        {shouldShowGenerateInvoice() && (
          <Button
            color="success"
            onClick={handleGenerateInvoice}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: '#4CAF50',
              color: '#fff',
              '&:hover': {
                bgcolor: '#4CAF50',
                filter: 'brightness(0.9)'
              }
            }}
          >
            Gen Invoice
          </Button>
        )}
        {availableActions.map((actionItem) => {
          // Use white text for dark colours, black for light ones
          const darkColors = ['#f44336', '#66BB6A', '#9C27B0', '#1976D2', '#42A5F5'];
          const textColor = actionItem.customColor && darkColors.includes(actionItem.customColor) ? '#fff' : '#000';
          return (
            <Button 
              key={actionItem.value}
              color={actionItem.color}
              onClick={() => handleOpen(actionItem.value)}
              size="small"
              sx={{ 
                minWidth: 'auto', 
                px: 1, 
                py: 0.25, 
                fontSize: '0.75rem',
                textTransform: 'none',
                ...(actionItem.customColor && {
                  bgcolor: actionItem.customColor,
                  color: textColor,
                  '&:hover': {
                    bgcolor: actionItem.customColor,
                    filter: 'brightness(0.9)'
                  }
                })
              }}
            >
              {actionItem.label}
            </Button>
          );
        })}
      </ButtonGroup>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getDialogMessage()}
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              id="notes"
              label="Notes"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

WorkflowActions.propTypes = {
  order: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func,
  onCreateProof: PropTypes.func,
  onCreateQuote: PropTypes.func,
  onClose: PropTypes.func,
  renderAsMenuItems: PropTypes.bool
};

export default WorkflowActions; 